import React, { useEffect } from 'react';

import {
    SquarespaceAssets,
    SquarespaceHandlePayments,
    SquarespaceNeeds,
} from '__generated__/types';
import CheckboxList from 'common/components/CheckboxList';
import RadioList from 'common/components/RadioList';
import Text from 'common/components/Text';
import TextArea from 'common/components/TextArea';
import TextList from 'common/components/TextList';
import { handlePromise } from 'common/utils/Bugsnag';
import { ObservableField, ObservableForm } from 'projects/components/ObservableForm';
import { trackViewedBriefEvent } from 'projects/events';
import TimelineField from './TimelineField';

interface Fields {
    title: string;
    needs: SquarespaceNeeds;
    handlePayments: SquarespaceHandlePayments;
    description: string;
    url: string;
    assets: SquarespaceAssets[];
    inspirationUrls: string[];
    timeline: any;
}

interface Props<T> {
    form: ObservableForm<T>;
    brief: {
        id: string;
    };
}

const DetailsPage: <T extends Fields>(props: Props<T>) => React.ReactElement<Props<T>> = ({
    form,
    brief,
}) => {
    useEffect(() => {
        trackViewedBriefEvent({
            id: brief.id,
            page: 'Details',
        });
    }, []);

    return (
        <div className="content-setting">
            <ObservableField
                form={form}
                name="title"
                title={__('Project name')}
                required
                render={() => <Text fill={true} {...form.attach('title')} />}
            />

            <ObservableField
                form={form}
                name="needs"
                title={__('What describes your needs the best?')}
                render={() => (
                    <RadioList
                        options={[
                            {
                                value: SquarespaceNeeds.NEW_SITE,
                                label: __('Create a brand new site on Squarespace.'),
                            },
                            {
                                value: SquarespaceNeeds.RECREATE_SITE,
                                label: __(
                                    'Recreate an existing site from another service on Squarespace.'
                                ),
                            },
                            {
                                value: SquarespaceNeeds.MINOR_CHANGES,
                                label: __(
                                    'Make minor changes to an existing Squarespace site. (Add or update a page, form, or similar.)'
                                ),
                            },
                            {
                                value: SquarespaceNeeds.FINISH_TRIAL_SITE,
                                label: __('Finish creating an existing Squarespace trial site.'),
                            },
                            {
                                value: SquarespaceNeeds.REDESIGN_SITE,
                                label: __(
                                    'Completely redesign an existing Squarespace site. (New fonts, colors or styles.)'
                                ),
                            },
                            {
                                value: SquarespaceNeeds.MAJOR_CHANGES,
                                label: __(
                                    'Make major changes to an existing Squarespace site. (Add or update multiple pages.)'
                                ),
                            },
                        ]}
                        {...form.attach('needs')}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="handlePayments"
                title={__(
                    'Do you want to sell products or services, or accept donations on your site?'
                )}
                render={() => (
                    <RadioList
                        options={[
                            {
                                value: SquarespaceHandlePayments.YES,
                                label: __('Yes'),
                            },
                            {
                                value: SquarespaceHandlePayments.NO,
                                label: __('No'),
                            },
                        ]}
                        {...form.attach('handlePayments')}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="description"
                title={__("What's your website for?")}
                render={() => (
                    <TextArea
                        style={{ minHeight: 200 }}
                        placeholder={__(
                            "For example, you're starting a bakery and need a website to promote the business. You'll also sell baked goods on the website. You want customers to get inspired by your story and come to your store."
                        )}
                        fill={true}
                        {...form.attach('description')}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="url"
                title={__("If you have an existing website, what's the URL?")}
                render={() => (
                    <Text fill={true} {...form.attach('url')} placeholder="www.example.com" />
                )}
            />

            <ObservableField
                form={form}
                name="assets"
                title={__('Do you already have any of the following? Select all that apply.')}
                render={() => (
                    <CheckboxList
                        options={[
                            {
                                value: SquarespaceAssets.IMAGES_VIDEO,
                                label: __('Image or Video'),
                            },
                            { value: SquarespaceAssets.COPY, label: __('Copy') },
                            { value: SquarespaceAssets.LOGO, label: __('Logo') },
                        ]}
                        {...form.attach('assets')}
                    />
                )}
            />

            <ObservableField
                form={form}
                name="inspirationUrls"
                title={__("Are there any websites you'd like to use for inspiration?")}
                render={() => (
                    <TextList {...form.attach('inspirationUrls')} placeholder="www.example.com" />
                )}
            />

            <ObservableField
                form={form}
                name="timeline"
                title={__('Do you have a strict deadline for this project?')}
                render={() => (
                    <TimelineField
                        value={form.get('timeline')}
                        onChange={date => {
                            handlePromise(form.set('timeline', date), 'TimeAndBudgetPage');
                        }}
                    />
                )}
            />
        </div>
    );
};

export default DetailsPage;
